import React, { useMemo, useState } from 'react';
import { Send, CalendarClock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { crmService } from '@/lib/api';
import { INTERACTION_TYPES, StateBadge, toLocalInputValue } from './crmHelpers';

const empty = { type: 'call', contact_value: '', response_type_id: '', notes: '', promise_date: '', promise_amount: '', send: false, message: '', subject: '', schedule: false, schedule_call_at: '', schedule_notes: '', schedule_assigned_to: '' };

const CrmInteractionForm = ({ debtorId, contacts, settings, users, currentUserEmail, onCreated }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const responses = settings.response_types.filter((r) => r.is_active);
  const response = responses.find((r) => r.id === form.response_type_id);
  const canSend = form.type === 'whatsapp' || form.type === 'email';
  const contactOptions = useMemo(() => contacts.filter((c) => c.is_valid && (form.type === 'email' ? c.type === 'email' : c.type === 'phone')), [contacts, form.type]);

  const submit = async () => {
    if (form.type !== 'note' && !form.response_type_id && !(form.send && canSend)) { toast.error('Seleccioná el tipo de respuesta'); return; }
    if (response?.requires_promise && (!form.promise_date || !form.promise_amount)) { toast.error('La promesa de pago requiere fecha y monto'); return; }
    if (form.send && (!form.contact_value || !form.message.trim())) { toast.error('Elegí el contacto y escribí el mensaje'); return; }
    if (form.schedule && !form.schedule_call_at) { toast.error('Indicá fecha y hora de la llamada'); return; }
    setSaving(true);
    try {
      const payload = {
        type: form.type, contact_value: form.contact_value || null, response_type_id: form.response_type_id || null, notes: form.notes || null,
        promise_date: response?.requires_promise ? form.promise_date : null, promise_amount: response?.requires_promise ? Number(form.promise_amount) : null,
        send: form.send && canSend, message: form.send ? form.message : null, subject: form.send && form.type === 'email' ? form.subject || null : null,
        schedule_call_at: form.schedule ? new Date(form.schedule_call_at).toISOString() : null,
        schedule_notes: form.schedule ? form.schedule_notes || null : null, schedule_assigned_to: form.schedule ? form.schedule_assigned_to || null : null,
      };
      const res = await crmService.createInteraction(debtorId, payload);
      const st = res.data.interaction.send_status;
      toast.success(st === 'sent' ? 'Mensaje enviado y gestión registrada' : st === 'failed' ? 'Gestión registrada, pero el envío falló' : 'Gestión registrada');
      setForm(empty);
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo registrar la gestión');
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-testid="crm-interaction-form">
      <div className="flex flex-wrap gap-1.5">
        {INTERACTION_TYPES.map((t) => {
          const Icon = t.icon; const active = form.type === t.id;
          return (
            <button key={t.id} type="button" onClick={() => set({ type: t.id, send: false, contact_value: '' })} data-testid={`crm-type-${t.id}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
              <Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>

      {form.type !== 'note' && (
        <div className="grid sm:grid-cols-2 gap-2">
          <Select value={form.contact_value || '__none__'} onValueChange={(v) => set({ contact_value: v === '__none__' ? '' : v })}>
            <SelectTrigger className="h-9 text-xs bg-white" data-testid="crm-contact-select"><SelectValue placeholder="Contacto usado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sin especificar</SelectItem>
              {contactOptions.map((c) => <SelectItem key={c.id} value={c.value}>{c.is_confirmed ? '✓ ' : ''}{c.value} · {c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.response_type_id || '__none__'} onValueChange={(v) => set({ response_type_id: v === '__none__' ? '' : v })}>
            <SelectTrigger className="h-9 text-xs bg-white" data-testid="crm-response-select"><SelectValue placeholder="Tipo de respuesta *" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Tipo de respuesta…</SelectItem>
              {responses.map((r) => <SelectItem key={r.id} value={r.id}><StateBadge state={r} /></SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {response?.requires_promise && (
        <div className="grid grid-cols-2 gap-2 rounded-lg border border-violet-200 bg-violet-50 p-2" data-testid="crm-promise-fields">
          <div><label className="text-[11px] text-violet-800">Fecha prometida *</label><Input type="date" value={form.promise_date} onChange={(e) => set({ promise_date: e.target.value })} className="h-8 text-xs bg-white" data-testid="crm-promise-date" /></div>
          <div><label className="text-[11px] text-violet-800">Monto prometido *</label><Input type="number" min="0" value={form.promise_amount} onChange={(e) => set({ promise_amount: e.target.value })} className="h-8 text-xs bg-white" placeholder="$" data-testid="crm-promise-amount" /></div>
        </div>
      )}

      {form.type === 'sms' && (
        <p className="text-[11px] text-slate-500 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5" data-testid="crm-sms-hint">Los SMS se registran como gestión. El envío automático requiere configurar un proveedor de SMS.</p>
      )}

      {canSend && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2 space-y-2">
          <label className="flex items-center justify-between text-xs text-emerald-900 cursor-pointer">
            <span className="inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />Enviar {form.type === 'whatsapp' ? 'WhatsApp' : 'email'} desde MacroPago</span>
            <Switch checked={form.send} onCheckedChange={(v) => set({ send: v })} data-testid="crm-send-switch" />
          </label>
          {form.send && (
            <>
              {form.type === 'email' && <Input value={form.subject} onChange={(e) => set({ subject: e.target.value })} placeholder="Asunto" className="h-8 text-xs bg-white" data-testid="crm-send-subject" />}
              <Textarea value={form.message} onChange={(e) => set({ message: e.target.value })} placeholder="Mensaje a enviar…" rows={3} className="text-xs bg-white" data-testid="crm-send-message" />
            </>
          )}
        </div>
      )}

      <Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder={form.type === 'note' ? 'Nota interna *' : 'Observaciones de la gestión'} rows={2} className="text-sm bg-white" data-testid="crm-notes" />

      <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-2 space-y-2">
        <label className="flex items-center justify-between text-xs text-sky-900 cursor-pointer">
          <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />Agendar próxima llamada</span>
          <Switch checked={form.schedule} onCheckedChange={(v) => set({ schedule: v, schedule_call_at: v && !form.schedule_call_at ? toLocalInputValue(new Date(Date.now() + 86400000)) : form.schedule_call_at })} data-testid="crm-schedule-switch" />
        </label>
        {form.schedule && (
          <div className="grid sm:grid-cols-3 gap-2">
            <Input type="datetime-local" value={form.schedule_call_at} onChange={(e) => set({ schedule_call_at: e.target.value })} className="h-8 text-xs bg-white" data-testid="crm-schedule-at" />
            <Select value={form.schedule_assigned_to || currentUserEmail || '__me__'} onValueChange={(v) => set({ schedule_assigned_to: v })}>
              <SelectTrigger className="h-8 text-xs bg-white" data-testid="crm-schedule-assignee"><SelectValue placeholder="Asignar a" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={form.schedule_notes} onChange={(e) => set({ schedule_notes: e.target.value })} placeholder="Motivo" className="h-8 text-xs bg-white" data-testid="crm-schedule-notes" />
          </div>
        )}
      </div>

      <Button onClick={submit} disabled={saving || (form.type === 'note' && !form.notes.trim())} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="crm-interaction-submit">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}{form.send ? 'Enviar y registrar' : 'Registrar gestión'}
      </Button>
    </div>
  );
};

export default CrmInteractionForm;
